import BaseModalStore from '@src/components/modal-container/base-modal-store';
import Toast from '@src/components/toast';
import { observable } from 'mobx';
import { autobind } from 'core-decorators';
import { noop } from '@src/utils/common-util';

/** 五门派草稿键（与 mo-poster SECT_GROUP_NUMBERS 前缀一致的 5×2 映射） */
const SECT_KEYS = ['tianwang', 'qianji', 'jinzhong', 'yishui', 'wudang'];

/** 性别草稿键 */
const GENDER_KEYS = ['male', 'female'];

/** 门派或性别草稿缺失时的补全提示（PRD 边界-6-5） */
const INCOMPLETE_DRAFT_MESSAGE = '请选择门派和性别';

/**
 * 门派/性别编辑弹窗 Store：持有门派与性别草稿（store-autonomy：草稿只在弹窗内读写、不持久化）。
 * 确认值、天王+男缺省决策与 5×2 视觉映射归页面 Store；
 * 本 Store 只回显打开时快照、校验补全并经 onConfirm 提交，不接收 pageStore 引用。
 */
class RoleEditorModalStore extends BaseModalStore {
  /** 草稿门派键（tianwang/qianji/jinzhong/yishui/wudang） */
  @observable draftSect = '';

  /** 草稿性别键（male/female） */
  @observable draftGender = '';

  /** 打开时的确认值快照，cancelDraft 的恢复来源 */
  snapshotSect = '';

  snapshotGender = '';

  getSect = () => '';

  getGender = () => '';

  onConfirm = noop;

  constructor(options = {}) {
    super(options);
    // 二次合并：子类字段初始化会覆盖 super 构造中的同名注入，需在字段初始化后重新落位
    Object.assign(this, options);
  }

  /**
   * 打开弹窗前由页面 Store 调用：从注入的 getter 拉取当前门派/性别确认值，
   * 草稿与恢复快照都初始化为该确认值；来源为空时保持空草稿（缺省默认由页面侧决策，弹窗不擅自补值）。
   *
   * @returns {undefined}
   */
  @autobind
  syncSnapshot() {
    const sect = this.getSect();
    const gender = this.getGender();
    const validSect = SECT_KEYS.indexOf(sect) !== -1 ? sect : '';
    const validGender = GENDER_KEYS.indexOf(gender) !== -1 ? gender : '';
    this.draftSect = validSect;
    this.draftGender = validGender;
    this.snapshotSect = validSect;
    this.snapshotGender = validGender;
    return undefined;
  }

  /**
   * 更新草稿门派；非法门派键忽略，草稿保持原值。
   *
   * @param {string} sect 门派键（tianwang/qianji/jinzhong/yishui/wudang）。
   * @returns {undefined}
   */
  @autobind
  selectDraftSect(sect) {
    if (SECT_KEYS.indexOf(sect) === -1) {
      return undefined;
    }

    this.draftSect = sect;
    return undefined;
  }

  /**
   * 更新草稿性别；非法性别键忽略，草稿保持原值。
   *
   * @param {string} gender 性别键（male/female）。
   * @returns {undefined}
   */
  @autobind
  selectDraftGender(gender) {
    if (GENDER_KEYS.indexOf(gender) === -1) {
      return undefined;
    }

    this.draftGender = gender;
    return undefined;
  }

  /**
   * 确认草稿：门派与性别齐备才经 onConfirm 提交并标准关闭；
   * 缺失时不提交、保持编辑层并 Toast 提示补全（边界-6-5）。
   *
   * @returns {boolean} 是否确认成功。
   */
  @autobind
  confirmDraft() {
    if (!this.draftSect || !this.draftGender) {
      Toast.fail(INCOMPLETE_DRAFT_MESSAGE);
      return false;
    }

    this.onConfirm(this.draftSect, this.draftGender);
    this.onClickClose();
    return true;
  }

  /**
   * 取消编辑：恢复打开时的确认值快照并标准关闭，不回调页面 Store、不触发任何持久化。
   *
   * @returns {undefined}
   */
  @autobind
  cancelDraft() {
    this.draftSect = this.snapshotSect;
    this.draftGender = this.snapshotGender;
    this.onClickClose();
    return undefined;
  }
}

export default RoleEditorModalStore;
